import { useState } from 'react'

import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  Stack,
  HStack,
  Text,
} from '@chakra-ui/react'
import { Field } from '@micin-jp/chicken-schema'
import { Certification } from 'src/components/icon'
import { useWorksheetValueContext } from 'src/features/worksheet/context/WorksheetValue'
import { colors } from 'src/lib/chakra-theme/foundations/colors'

import { StickyPopover } from '../StickyPopover/StickyPopover'
import { WorksheetFieldHistory } from '../WorksheetFieldHistory/WorksheetFieldHistory'

type Props = {
  field: Field
  index: number
  isDisabled: boolean
  showCertifiedCopyIcon?: boolean
  errorMessage?: string
  children: React.ReactNode
}

/** ワークシートの各フィールドで呼び出すHelperコンポーネント
 *
 * ChakraUIのFormControlを使用している。
 * フィールドの履歴表示なども担う。
 */
export const WorksheetFieldFormControl: React.FC<Props> = ({
  field,
  index,
  isDisabled,
  showCertifiedCopyIcon,
  errorMessage,
  children,
}) => {
  const [inputting, setInputting] = useState(false)
  const { observationType, getEditableHospitalTypes } =
    useWorksheetValueContext()

  const isDocumentExchange = observationType === 'DocumentExchange'
  const editableHospitals = isDocumentExchange
    ? getEditableHospitalTypes(field.typeDef)
    : { main: false, partner: false }

  return (
    <FormControl
      onFocus={() => setInputting(true)}
      onBlur={() => setInputting(false)}
      isDisabled={isDisabled}
      isInvalid={!inputting && !!errorMessage}
    >
      <Stack mb="6px" spacing="3px">
        <HStack align="center" spacing="3">
          <HStack spacing="0.5">
            <FormLabel m="0" className="field_name">
              {field.name}
            </FormLabel>
            {showCertifiedCopyIcon && (
              <Certification color={colors.green[500]} size="12px" />
            )}
          </HStack>
          {isDocumentExchange && editableHospitals.main && (
            <Flex
              align="center"
              justify="center"
              bg="blue.100"
              h="20px"
              w="80px"
              flexShrink={0}
            >
              <Text fontSize="xs" fontWeight="medium" color="black">
                実施
              </Text>
            </Flex>
          )}
          {isDocumentExchange && editableHospitals.partner && (
            <Flex
              align="center"
              justify="center"
              bg="green.100"
              h="20px"
              w="80px"
              flexShrink={0}
            >
              <Text fontSize="xs" fontWeight="medium" color="black">
                パートナー
              </Text>
            </Flex>
          )}
          <WorksheetFieldHistory field={field} index={index} />
          <Box flex={1}>
            <StickyPopover field={field} index={index} />
          </Box>
        </HStack>
        {!!field.description && (
          <FormHelperText color="gray.600" fontSize="xs" mt="0" mb="0">
            {field.description}
          </FormHelperText>
        )}
      </Stack>
      <Box>{children}</Box>
      {!!errorMessage && (
        <FormErrorMessage color="red.500" fontSize="xs" mt="1.5" mb="0">
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  )
}
